import React, { useEffect, useState, useRef } from "react";
import Navbar from "../components/Navbar";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import anime from "animejs";
import ReactMarkdown from "react-markdown";
import { transformStrapiArticle, getStrapiURL } from "../utils/strapiUtils";
import SEO from "../components/SEO";

const CustomBlockquote = ({ children }) => {
  return (
    <figure className="my-4">
      <blockquote className="blockquote  ">{children}</blockquote>
    </figure>
  );
};

const CodeBlock = ({ code }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="position-relative mb-3 w-100">
      <pre className="line-numbers p-3 rounded mb-0">
        <code className="language-html">{code}</code>
      </pre>
      <button
        className={`btn btn-sm position-absolute top-0 end-0 m-2 ${
          copied ? "btn-success" : "btn-light"
        }`}
        onClick={handleCopy}
        style={{
          zIndex: 10,
          padding: "0.25rem 0.5rem",
          fontSize: "0.75rem",
          right: "0.5rem",
          top: "0.5rem",
        }}
      >
        {copied ? "Copied!" : "Copy"}
      </button>
    </div>
  );
};

const BlogDetails = () => {
  const { slug } = useParams();
  const [blogData, setBlogData] = useState(null);
  const observerRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (slug) {
      getData(slug);
    }
  }, [slug]);

  const getData = async (slug) => {
    try {
      const { data } = await axios.get(getStrapiURL("/api/articles"), {
        params: {
          "filters[slug][$eq]": slug,
          "populate[blocks][populate]": "*",
          "populate[cover]": true,
          "populate[author]": true,
          "populate[category]": true,
        },
      });

      if (data.data && data.data.length > 0) {
        setBlogData(transformStrapiArticle(data.data[0]));
      } else {
        toast.error("Failed to load blog post.");
      }
    } catch (error) {
      console.error("Error loading blog details:", error);
      if (error.response) {
        console.error("Server Error Data:", error.response.data);
        console.error("Server Error Status:", error.response.status);
      }
      toast.error(
        `Error: ${
          error.details?.message || error.message || "Something went wrong"
        }`,
      );
    }
  };
  // ... (rest of code)

  const formatDate = (dateString) => {
    if (!dateString) return "Date unknown";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return "Date invalid";
    }
  };

  // Setup scroll-based animations
  useEffect(() => {
    if (!blogData) return;

    // Initialize Intersection Observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const element = entry.target;

            // Different animations based on element class
            if (element.classList.contains("blog-title")) {
              anime({
                targets: element,
                translateY: [-50, 0],
                opacity: [0, 1],
                duration: 1000,
                easing: "easeOutExpo",
              });
            } else if (element.classList.contains("blog-meta")) {
              anime({
                targets: element,
                translateY: [20, 0],
                opacity: [0, 1],
                duration: 800,
                easing: "easeOutQuad",
                delay: 200,
              });
            } else if (element.classList.contains("blog-cover")) {
              anime({
                targets: element,
                opacity: [0, 1],
                scale: [1.05, 1],
                duration: 1200,
                easing: "easeOutQuart",
              });
            } else if (element.classList.contains("blog-content-block")) {
              const index = parseInt(element.dataset.index);
              anime({
                targets: element,
                translateY: [30, 0],
                opacity: [0, 1],
                duration: 800,
                easing: "easeOutQuad",
                delay: index * 50,
              });
            }

            // Stop observing after animation
            observerRef.current.unobserve(element);
          }
        });
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.1,
      },
    );

    // Observe elements
    const titleElement = document.querySelector(".blog-title");
    if (titleElement) observerRef.current.observe(titleElement);

    const metaElement = document.querySelector(".blog-meta");
    if (metaElement) observerRef.current.observe(metaElement);

    const coverElement = document.querySelector(".blog-cover");
    if (coverElement) observerRef.current.observe(coverElement);

    const contentBlocks = document.querySelectorAll(".blog-content-block");
    contentBlocks.forEach((block) => observerRef.current.observe(block));

    // Cleanup observer
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [blogData]);

  return (
    <>
      <main className="page-wrapper mb-5" ref={containerRef}>
        <Navbar />
        <section className="container py-5 mt-lg-5 mb-md-2 mb-lg-3 mb-xl-4">
          <nav aria-label="breadcrumb">
            <ol className="pt-lg-3 pb-lg-4 pb-2 breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/">Home</Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                {slug}
              </li>
            </ol>
          </nav>

          {blogData && (
            <>
              <h1 className="display-4 text-center pb-2 pb-lg-3 blog-title">
                {blogData.title}
              </h1>
              <SEO
                title={blogData.title}
                description={blogData.description}
                image={
                  blogData.content?.[0]?.type === "image"
                    ? blogData.content[0].value
                    : undefined
                }
                type="article"
                schema={{
                  "@context": "https://schema.org",
                  "@type": "BlogPosting",
                  headline: blogData.title,
                  image:
                    blogData.content?.[0]?.type === "image"
                      ? [blogData.content[0].value]
                      : [],
                  datePublished: blogData.publishedAt,
                  dateModified: blogData.updatedAt || blogData.publishedAt,
                  author: [
                    {
                      "@type": "Person",
                      name: blogData.author || "Hiren Hadiya",
                      url: "https://hadiya.in/about",
                    },
                  ],
                }}
              />
              <div className="d-flex flex-wrap align-items-center justify-content-center mt-n2 blog-meta">
                <span className="fs-sm text-body-secondary mt-2">
                  {formatDate(blogData.createdAt)}
                </span>
                <span className="fs-xs opacity-20 mt-2 mx-3">|</span>
                {blogData?.tags?.map((val, ind) => (
                  <a
                    className="badge text-nav fs-xs border mt-2"
                    href="#"
                    key={ind}
                  >
                    {val}
                  </a>
                ))}
              </div>
            </>
          )}
        </section>

        {/* Cover image */}
        {blogData &&
          blogData?.content?.length > 0 &&
          blogData.content[0].type === "image" && (
            <section className="container mb-5">
              <img
                className="img-fluid rounded-3 w-100"
                src={blogData.content[0].value}
                alt={blogData.title}
                style={{ objectFit: "cover" }}
              />
            </section>
          )}

        {/* Blog content */}
        <section className="container pt-5 mt-md-2 mt-lg-3 mt-xl-4">
          <div className="row justify-content-center pt-xxl-2">
            <div className="col-lg-9 col-xl-8">
              {blogData?.content?.map((v, i) => {
                if (i === 0 && v.type === "image") return null;
                return (
                  <div
                    className="mb-4 blog-content-block"
                    key={i}
                    data-index={i}
                  >
                    {v.type === "image" ? (
                      <figure className="figure text-center w-100">
                        <img
                          src={v.value}
                          alt={`img-${i}`}
                          className="figure-img img-fluid rounded"
                          style={{ maxHeight: "500px", objectFit: "contain" }}
                        />
                        {v.caption && (
                          <figcaption className="figure-caption">
                            {v.caption}
                          </figcaption>
                        )}
                      </figure>
                    ) : v.type === "code" ? (
                      <CodeBlock code={v.value} />
                    ) : v.type === "quote" ? (
                      <figure className="">
                        <blockquote className="blockquote">
                          <p className="">"{v.value}"</p>
                        </blockquote>
                        {v.author && (
                          <figcaption className="blockquote-footer mt-2">
                            {v.author}
                          </figcaption>
                        )}
                      </figure>
                    ) : v.type === "slider" ? (
                      <div
                        id={`carousel-${i}`}
                        className="carousel slide mb-4"
                        data-bs-ride="carousel"
                      >
                        <div className="carousel-inner rounded">
                          {v.value.map((imgUrl, imgIdx) => (
                            <div
                              className={`carousel-item ${
                                imgIdx === 0 ? "active" : ""
                              }`}
                              key={imgIdx}
                            >
                              <img
                                src={imgUrl}
                                className="d-block w-100"
                                alt={`slide-${imgIdx}`}
                                style={{
                                  maxHeight: "500px",
                                  objectFit: "cover",
                                }}
                              />
                            </div>
                          ))}
                        </div>
                        {v.value.length > 1 && (
                          <>
                            <button
                              className="carousel-control-prev"
                              type="button"
                              data-bs-target={`#carousel-${i}`}
                              data-bs-slide="prev"
                            >
                              <span
                                className="carousel-control-prev-icon"
                                aria-hidden="true"
                              ></span>
                              <span className="visually-hidden">Previous</span>
                            </button>
                            <button
                              className="carousel-control-next"
                              type="button"
                              data-bs-target={`#carousel-${i}`}
                              data-bs-slide="next"
                            >
                              <span
                                className="carousel-control-next-icon"
                                aria-hidden="true"
                              ></span>
                              <span className="visually-hidden">Next</span>
                            </button>
                          </>
                        )}
                      </div>
                    ) : v.type === "text" ? (
                      <div className="text-justify blog-text">
                        <ReactMarkdown
                          components={{
                            blockquote: CustomBlockquote,
                            pre: ({ children }) => <>{children}</>,
                            code: ({
                              node,
                              inline,
                              className,
                              children,
                              ...props
                            }) => {
                              const match = /language-(\w+)/.exec(
                                className || "",
                              );
                              return !inline ? (
                                <CodeBlock
                                  code={String(children).replace(/\n$/, "")}
                                />
                              ) : (
                                <code className={className} {...props}>
                                  {children}
                                </code>
                              );
                            },
                          }}
                        >
                          {v.value}
                        </ReactMarkdown>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      </main>

      {/* Subscription section */}
      <section
        className="container mb-2 mb-md-3 mb-xl-4 pb-2"
        data-bs-theme="dark"
      >
        <div className="position-relative bg-dark rounded-5 overflow-hidden p-md-5 p-4">
          <div
            className="position-absolute top-0 start-0 w-100 h-100"
            style={{ backgroundColor: "rgba(255, 255, 255, .03)" }}
          ></div>
          <div className="position-relative p-xl-5 p-md-4 py-4 px-sm-3">
            <h2 className="h1 pb-md-4 pb-3 mt-n2">
              Stay up to date with important news!
            </h2>
            <div className="row gy-md-5 gy-4 gx-xl-5">
              <div className="col-lg-7">
                <div className="row row-cols-sm-3 row-cols-2 gy-lg-4 gy-3 gx-xl-4 gx-sm-3 gx-2">
                  {[
                    "Advertising Updates",
                    "Daily Newsletter",
                    "Week in Review",
                    "Inspiration",
                    "Psychology",
                    "Design",
                  ].map((label, idx) => (
                    <div className="col" key={idx}>
                      <div className="form-check mb-0">
                        <label className="form-check-label fs-base fw-medium">
                          {label}
                        </label>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          defaultChecked={
                            label === "Advertising Updates" ||
                            label === "Design"
                          }
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="col-lg-5">
                <div className="input-group rounded-pill">
                  <input
                    className="form-control"
                    type="text"
                    placeholder="Your email"
                  />
                  <button
                    className="btn btn-primary rounded-pill"
                    type="button"
                  >
                    Subscribe
                  </button>
                </div>
                <div className="form-text mt-3 fs-sm">
                  * Yes, I agree to the <a href="#">terms</a> and{" "}
                  <a href="#">privacy policy</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default BlogDetails;
